import { AxiosResponse } from 'axios';
import { ResponseWithPagination } from '@/types';
import {
  InterviewNoteType,
  InterviewReviewType,
  InterviewScore,
  InterviewTechnicalChallenge,
  InterviewType,
  TechnicalChallenge,
  TechnicalChallengeInterviewReviewers,
  TechnicalChallengeSuggestions,
} from '@/types/api';
import { InterviewTypes } from '@/utils/interviews';
import { supabase } from '@/lib/supabase';
import { asResponse, throwIfError, paginationRange } from '@/lib/supabase-helpers';

// ---------------------------------------------------------------------------
// Mappers: convert snake_case Supabase rows → camelCase shapes expected by UI
// ---------------------------------------------------------------------------

function mapUser(row: any): any {
  if (!row) return { id: '', name: '', email: '', personId: null, person: null, roles: [], auth0Id: null, slackId: null, createdAt: '', updatedAt: '', deletedAt: null };
  return {
    id: row.id,
    name: `${row.first_name ?? ''} ${row.last_name ?? ''}`.trim(),
    email: row.email ?? '',
    personId: row.id,
    person: row,
    roles: [],
    auth0Id: null,
    slackId: row.slack_id ?? null,
    createdAt: row.created_at ?? '',
    updatedAt: row.updated_at ?? '',
    deletedAt: row.deleted_at ?? null,
  };
}

function mapPerson(row: any): any {
  if (!row) return null;
  return {
    id: row.id,
    firstName: row.first_name,
    lastName: row.last_name,
    email: row.email ?? null,
    pictureUrl: row.picture_url ?? null,
    linkedinUrl: row.linkedin_url ?? '',
    githubUrl: row.github_url ?? null,
    developerType: row.developer_type ?? null,
    experience: row.experience ?? '',
    englishLevel: row.english_level ?? null,
    availability: row.availability ?? null,
    salaryExpectation: row.salary_expectation ?? '',
    source: row.source ?? '',
    referredBy: row.referred_by ?? '',
    status: row.status ?? null,
    rating: row.overall_rating ?? 0,
    communicationSkillsRating: row.communication_rating ?? 0,
    softSkillsRating: row.soft_skills_rating ?? 0,
    technicalSkillsRating: row.technical_rating ?? 0,
    summary: row.summary ?? null,
    country: row.country ?? null,
    state: row.state ?? null,
    city: row.city ?? '',
    mobilePhone: row.phone ?? '',
    birthdate: row.birthdate ?? null,
    gender: row.gender ?? null,
    ethnicity: row.ethnicity ?? null,
    isTalentPool: row.is_talent_pool ?? false,
    hasFintechExperience: row.has_fintech_experience ?? false,
    skills: row.skills ?? [],
    mainTechStack: row.mainTechStack ?? [],
    createdAt: row.created_at ?? '',
    updatedAt: row.updated_at ?? '',
    deletedAt: row.deleted_at ?? null,
    ownerId: row.owner_id ?? '',
  };
}

function mapScore(row: any): any {
  return {
    id: row.id,
    scoreType: row.score_type,
    score: row.score,
    technicalChallengeCompetencyScoreId: row.competency_score_id ?? null,
  };
}

function mapReview(row: any): any {
  return {
    id: row.id,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    note: row.note ?? null,
    aiGeneratedOverview: row.ai_generated_overview ?? null,
    user: mapUser(row.user),
    score: (row.scores ?? []).map(mapScore),
  };
}

function mapNote(row: any): any {
  return {
    id: row.id,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    note: row.note ?? null,
    user: mapUser(row.user),
  };
}

function mapQuestion(row: any): any {
  return {
    id: row.id,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    questionText: row.question_text,
    answer: row.answer ?? undefined,
  };
}

function mapTechnicalChallenge(row: any): any {
  if (!row) return null;
  return {
    id: row.id,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    name: row.name,
    description: row.description ?? null,
    url: row.url ?? '',
    isActive: row.is_active ?? false,
    gradingRubric: row.grading_rubric ?? null,
  };
}

function mapInterviewReviewer(row: any): any {
  if (!row) return null;
  return {
    id: row.id,
    person: mapPerson(row.person),
  };
}

function mapInterviewTechnicalChallenge(row: any): any {
  if (!row) return null;
  return {
    id: row.id,
    technicalChallenge: mapTechnicalChallenge(row.technicalChallenge),
    gitlabProjectUrl: row.gitlab_project_url ?? null,
    githubProjectUrl: row.github_project_url ?? null,
    technicalVideoUrl: row.technical_video_url ?? null,
    technicalDocumentTitle: row.technical_document_title ?? null,
    technicalDocumentContent: row.technical_document_content ?? null,
    status: row.status ?? null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    languageUsedByCandidate: row.language_used_by_candidate ?? null,
    skillId: row.skill_id ?? null,
    reviewers: (row.reviewers ?? []).map((r: any) => ({
      id: r.id,
      status: r.status,
      interviewReviewer: mapInterviewReviewer(r.interviewReviewer),
      interviewTechnicalChallenge: null,
    })),
  };
}

function mapInterview(row: any): InterviewType {
  const tcArr = row.technicalChallenge;
  const tc = Array.isArray(tcArr) ? tcArr[0] ?? null : tcArr ?? null;
  return {
    id: row.id,
    title: row.title,
    type: (row.type ?? '').toUpperCase(),
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    isAlreadySent: row.is_already_sent ?? false,
    recordingUrl: row.recording_url ?? null,
    person: mapPerson(row.person),
    questions: (row.questions ?? []).map(mapQuestion),
    notes: (row.notes ?? []).map(mapNote),
    reviews: (row.reviews ?? []).map(mapReview),
    technicalChallenge: mapInterviewTechnicalChallenge(tc),
    user: mapUser(null),
  } as any;
}

type UserParams = {
  candidateId: string;
};

type InterviewParams = {
  interviewId: string;
  interviewQuestionId?: string;
  interviewReviewId?: string;
  interviewNoteId?: string;
};

export type TechnicalChallengeParams = {
  technicalChallengeId?: string;
  skillId?: string;
};

export type GetPublicInterview = UserParams & InterviewParams;

export type PostPublicInterview = { interviewId: string; payload: FormData };

export type SendPublicInterview = {
  interviewId: string;
  interviewTechnicalChallengeId: string;
  payload: FormData;
};

export type CreateInterviewParams = UserParams & {
  title: string;
  type: InterviewTypes;
  userId: string;
  generateQuestionsByAI?: boolean;
};

export type DeleteInterviewParams = UserParams & InterviewParams;

export type UpdateInterviewParams = UserParams & InterviewParams & Pick<InterviewType, 'title'>;

type InterviewQuestionType = {
  questionText: string;
  generateQuestionByAI?: string;
};

export type AddInterviewQuestion = InterviewQuestionType & InterviewParams;

export type DeleteInterviewQuesiton = InterviewParams;

export type UpdateInterviewQuesiton = InterviewParams & InterviewQuestionType;

type CreateInterviewReviewType = Omit<InterviewReviewType, 'createdAt' | 'updatedAt'>;

type CreateTechnicalValidationInterviewReviewType = {
  name: string;
  additionalInfo?: string;
  scores: InterviewScore[];
  weight: number;
};

export type AddInterviewReview = Partial<CreateInterviewReviewType> & InterviewParams;

export type AddTechnicalValidationInterviewReview =
  Partial<CreateTechnicalValidationInterviewReviewType> & InterviewParams;

export type DeleteInterviewReview = InterviewParams;

export type UpdateInterviewReview = InterviewParams & Partial<CreateInterviewReviewType>;

export type UpdateTechnicalValidationInterviewReview = InterviewParams &
  Partial<CreateTechnicalValidationInterviewReviewType>;

type CreateInterviewNoteType = Omit<InterviewNoteType, 'createdAt' | 'updatedAt'>;

export type AddInterviewNote = Partial<CreateInterviewNoteType> & InterviewParams;

export type DeleteInterviewNote = InterviewParams;

export type UpdateInterviewNote = InterviewParams & Partial<CreateInterviewNoteType>;

const getCandidateInterviews = async ({ candidateId }: UserParams): Promise<AxiosResponse<InterviewType[]>> => {
  const result = await supabase
    .from('interviews')
    .select(`
      *,
      person:profiles!profile_id(*),
      questions:interview_questions(*),
      notes:interview_meeting_notes(*, user:profiles!author_id(*)),
      reviews:interview_reviews(*, scores:interview_scores(*), user:profiles!reviewer_id(*)),
      technicalChallenge:interview_technical_challenges(*, technicalChallenge:technical_challenges(*), reviewers:interview_technical_challenge_reviewers(*, interviewReviewer:interview_reviewers(*, person:profiles(*))))
    `)
    .eq('profile_id', candidateId);

  if (result.error) {
    console.error('[getCandidateInterviews] Supabase error:', result.error);
    throw result.error;
  }

  const rows = (result.data ?? []) as any[];
  return asResponse(rows.map(mapInterview));
};

const createInterview = async ({
  candidateId,
  title,
  type,
  userId,
  generateQuestionsByAI,
}: CreateInterviewParams): Promise<AxiosResponse<InterviewType>> => {
  const result = await supabase
    .from('interviews')
    .insert({
      profile_id: candidateId,
      title,
      type,
      interviewer_id: userId,
    })
    .select()
    .single();

  return asResponse(throwIfError(result));
};

const updateInterview = async ({
  candidateId,
  interviewId,
  title,
}: UpdateInterviewParams): Promise<AxiosResponse<any>> => {
  const result = await supabase
    .from('interviews')
    .update({ title })
    .eq('id', interviewId)
    .eq('profile_id', candidateId)
    .select()
    .single();

  return asResponse(throwIfError(result));
};

const deleteInterview = async ({ candidateId, interviewId }: DeleteInterviewParams): Promise<AxiosResponse<any>> => {
  const result = await supabase
    .from('interviews')
    .delete()
    .eq('id', interviewId)
    .eq('profile_id', candidateId);

  throwIfError(result);
  return asResponse(null);
};

const addQuestionInterview = async ({
  interviewId,
  questionText,
}: AddInterviewQuestion): Promise<AxiosResponse<any>> => {
  const result = await supabase
    .from('interview_questions')
    .insert({
      interview_id: interviewId,
      question_text: questionText,
    })
    .select()
    .single();

  return asResponse(throwIfError(result));
};

const deleteQuestionInterview = async ({
  interviewId,
  interviewQuestionId,
}: DeleteInterviewQuesiton): Promise<AxiosResponse<any>> => {
  const result = await supabase
    .from('interview_questions')
    .delete()
    .eq('id', interviewQuestionId)
    .eq('interview_id', interviewId);

  throwIfError(result);
  return asResponse(null);
};

const updateQuestionInterview = async ({
  interviewId,
  interviewQuestionId,
  questionText,
}: UpdateInterviewQuesiton): Promise<AxiosResponse<any>> => {
  const result = await supabase
    .from('interview_questions')
    .update({ question_text: questionText })
    .eq('id', interviewQuestionId)
    .eq('interview_id', interviewId)
    .select()
    .single();

  return asResponse(throwIfError(result));
};

type PublicInterviewQuestion = {
  id: string;
  questionText: string;
  answer?: string;
};

type PublicInterviewPerson = {
  email: string;
  firstName: string;
  lastName: string;
  status: string;
};

type PublicTechnicalChallenge = Omit<TechnicalChallenge, 'createdAt' | 'updatedAt'>;

type PublicInterviewTechnicalChallenge = Pick<InterviewTechnicalChallenge, 'id'> & {
  technicalChallenge: PublicTechnicalChallenge;
};

type PublicInterviewType = {
  id: string;
  title: string;
  type: string;
  hasSubmittedTechnicalChallenge: boolean;
  person: PublicInterviewPerson;
  questions: PublicInterviewQuestion[];
  interviewTechnicalChallenge?: PublicInterviewTechnicalChallenge;
};

const getCandidatePublicInterview = async ({
  candidateId,
  interviewId,
}: GetPublicInterview): Promise<AxiosResponse<PublicInterviewType>> => {
  const interviewResult = await supabase
    .from('interviews')
    .select(`
      id, title, type, is_already_sent,
      questions:interview_questions(id, question_text, answers:interview_answers(id, text_answer, attachment_url)),
      technicalChallenge:interview_technical_challenges(id, status, technicalChallenge:technical_challenges(id, name, description, url))
    `)
    .eq('id', interviewId)
    .eq('profile_id', candidateId)
    .single();

  const interview = throwIfError(interviewResult);

  const personResult = await supabase
    .from('profiles')
    .select('email, first_name, last_name, status')
    .eq('id', candidateId)
    .single();

  const person = throwIfError(personResult);

  const questions: PublicInterviewQuestion[] = ((interview as any).questions || []).map((q: any) => {
    const ans = (q.answers ?? [])[0];
    return {
      id: q.id,
      questionText: q.question_text,
      answer: ans ? { id: ans.id, textAnswer: ans.text_answer, attachmentUrl: ans.attachment_url } : undefined,
    };
  });

  const tc = (interview as any).technicalChallenge?.[0];
  const interviewTechnicalChallenge: PublicInterviewTechnicalChallenge | undefined = tc
    ? {
        id: tc.id,
        technicalChallenge: tc.technicalChallenge,
      }
    : undefined;

  const publicInterview: PublicInterviewType = {
    id: (interview as any).id,
    title: (interview as any).title,
    type: (interview as any).type,
    hasSubmittedTechnicalChallenge: tc?.status === 'SUBMITTED',
    person: {
      email: person.email,
      firstName: person.first_name,
      lastName: person.last_name,
      status: person.status,
    },
    questions,
    interviewTechnicalChallenge,
  };

  return asResponse(publicInterview);
};

const postCandidatePublicInterview = async ({
  interviewId,
  payload,
}: PostPublicInterview): Promise<AxiosResponse<void, any>> => {
  // Extract answers from FormData and upsert them into interview_questions
  const answers: { questionId: string; answer: string }[] = [];
  payload.forEach((value, key) => {
    if (key.startsWith('answers[')) {
      const match = key.match(/answers\[(\d+)\]\[(\w+)\]/);
      if (match) {
        const index = parseInt(match[1], 10);
        const field = match[2];
        if (!answers[index]) answers[index] = { questionId: '', answer: '' };
        if (field === 'questionId') answers[index].questionId = value as string;
        if (field === 'answer') answers[index].answer = value as string;
      }
    }
  });

  for (const ans of answers) {
    if (ans && ans.questionId) {
      const result = await supabase
        .from('interview_answers')
        .upsert({
          interview_question_id: ans.questionId,
          text_answer: ans.answer,
        }, { onConflict: 'interview_question_id' });

      throwIfError(result);
    }
  }

  return asResponse(undefined as unknown as void);
};

export type CreateTechnicalInterviewProps = {
  interviewId: string;
  technicalChallengeId: string;
  languageUsedByCandidate?: string; // Deprecated: kept for backward compatibility
  skillId?: string;
};

const createTechnicalInterview = async ({
  interviewId,
  technicalChallengeId,
  languageUsedByCandidate,
  skillId,
}: CreateTechnicalInterviewProps): Promise<AxiosResponse<void, any>> => {
  const insertData: Record<string, any> = {
    interview_id: interviewId,
    technical_challenge_id: technicalChallengeId,
  };

  if (skillId) {
    insertData.skill_id = skillId;
  } else if (languageUsedByCandidate) {
    insertData.language_used_by_candidate = languageUsedByCandidate;
  }

  const result = await supabase
    .from('interview_technical_challenges')
    .insert(insertData);

  throwIfError(result);
  return asResponse(undefined as unknown as void);
};

const sendCandidatePublicTechnicalInterview = async ({
  interviewId,
  interviewTechnicalChallengeId,
  payload,
}: SendPublicInterview): Promise<AxiosResponse<void, any>> => {
  // Extract fields from FormData
  const updateData: Record<string, any> = {};
  payload.forEach((value, key) => {
    updateData[key] = value;
  });

  const result = await supabase
    .from('interview_technical_challenges')
    .update(updateData)
    .eq('id', interviewTechnicalChallengeId)
    .eq('interview_id', interviewId);

  throwIfError(result);
  return asResponse(undefined as unknown as void);
};

const sendInterview = async ({
  candidateId,
  interviewId,
  technicalChallengeId,
  skillId,
}: DeleteInterviewParams & TechnicalChallengeParams): Promise<AxiosResponse<any>> => {
  // Mark interview as sent
  const updateData: Record<string, any> = {
    is_already_sent: true,
  };

  const result = await supabase
    .from('interviews')
    .update(updateData)
    .eq('id', interviewId)
    .eq('profile_id', candidateId)
    .select()
    .single();

  const interview = throwIfError(result);

  // If there's a technical challenge, link it
  if (technicalChallengeId) {
    const tcResult = await supabase
      .from('interview_technical_challenges')
      .insert({
        interview_id: interviewId,
        technical_challenge_id: technicalChallengeId,
        skill_id: skillId || null,
      });

    throwIfError(tcResult);
  }

  return asResponse(interview);
};

const addReviewInterview = async ({ interviewId, ...params }: AddInterviewReview): Promise<AxiosResponse<any>> => {
  const { data: { user } } = await supabase.auth.getUser();

  // Create the review record
  const reviewResult = await supabase
    .from('interview_reviews')
    .insert({
      interview_id: interviewId,
      reviewer_id: user?.id,
      note: params.note,
    })
    .select()
    .single();

  const review = throwIfError(reviewResult);

  // Insert scores linked to the review
  const scores = (params as any).score || [];
  if (scores.length > 0) {
    const scoreRows = scores.map((s: InterviewScore) => ({
      review_id: (review as any).id,
      score_type: s.scoreType,
      score: s.score,
    }));

    await supabase.from('interview_scores').insert(scoreRows);
  }

  return asResponse(review);
};

const deleteReviewInterview = async ({
  interviewId,
  interviewReviewId,
}: DeleteInterviewReview): Promise<AxiosResponse<any>> => {
  // Scores cascade-delete via review_id FK
  const result = await supabase
    .from('interview_reviews')
    .delete()
    .eq('id', interviewReviewId)
    .eq('interview_id', interviewId);

  throwIfError(result);
  return asResponse(null);
};

const updateReviewInterview = async ({
  interviewId,
  interviewReviewId,
  ...params
}: UpdateInterviewReview): Promise<AxiosResponse<any>> => {
  const updateData: Record<string, any> = {};
  if (params.note !== undefined) updateData.note = params.note;

  const result = await supabase
    .from('interview_reviews')
    .update(updateData)
    .eq('id', interviewReviewId)
    .eq('interview_id', interviewId)
    .select()
    .single();

  return asResponse(throwIfError(result));
};

const updateReviewAiOverview = async ({
  interviewId,
  interviewReviewId,
  ...params
}: UpdateInterviewReview): Promise<AxiosResponse<any>> => {
  const updateData: Record<string, any> = {};
  if ((params as any).aiGeneratedOverview !== undefined) {
    updateData.ai_generated_overview = (params as any).aiGeneratedOverview;
  }

  const result = await supabase
    .from('interview_reviews')
    .update(updateData)
    .eq('id', interviewReviewId)
    .eq('interview_id', interviewId)
    .select()
    .single();

  return asResponse(throwIfError(result));
};

const addTechnicalValidationReviewInterview = async ({
  interviewId,
  ...params
}: AddTechnicalValidationInterviewReview): Promise<AxiosResponse<any>> => {
  const { data: { user } } = await supabase.auth.getUser();

  // Create the review record
  const reviewResult = await supabase
    .from('interview_reviews')
    .insert({
      interview_id: interviewId,
      reviewer_id: user?.id,
      note: params.additionalInfo || null,
    })
    .select()
    .single();

  const review = throwIfError(reviewResult);

  // Insert scores linked to the review
  const scores = params.scores || [];
  if (scores.length > 0) {
    const scoreRows = scores.map((s: InterviewScore) => ({
      review_id: (review as any).id,
      score_type: s.scoreType,
      score: s.score,
    }));

    await supabase.from('interview_scores').insert(scoreRows);
  }

  return asResponse(review);
};

const updateTechnicalValidationReviewInterview = async ({
  interviewId,
  interviewReviewId,
  ...params
}: UpdateTechnicalValidationInterviewReview): Promise<AxiosResponse<any>> => {
  const updateData: Record<string, any> = {};
  if (params.additionalInfo !== undefined) updateData.note = params.additionalInfo;

  const result = await supabase
    .from('interview_reviews')
    .update(updateData)
    .eq('id', interviewReviewId)
    .eq('interview_id', interviewId)
    .select()
    .single();

  return asResponse(throwIfError(result));
};

const addNoteInterview = async ({ interviewId, ...params }: AddInterviewNote): Promise<AxiosResponse<any>> => {
  const { data: { user } } = await supabase.auth.getUser();

  const result = await supabase
    .from('interview_meeting_notes')
    .insert({
      interview_id: interviewId,
      author_id: user?.id,
      note: (params as any).note || (params as any).text || '',
    })
    .select()
    .single();

  return asResponse(throwIfError(result));
};

const deleteNoteInterview = async ({
  interviewId,
  interviewNoteId,
}: DeleteInterviewNote): Promise<AxiosResponse<any>> => {
  const result = await supabase
    .from('interview_meeting_notes')
    .delete()
    .eq('id', interviewNoteId)
    .eq('interview_id', interviewId);

  throwIfError(result);
  return asResponse(null);
};

const updateNoteInterview = async ({
  interviewId,
  interviewNoteId,
  ...params
}: UpdateInterviewNote): Promise<AxiosResponse<any>> => {
  const updateData: Record<string, any> = {};
  if ((params as any).note !== undefined) updateData.note = (params as any).note;
  if ((params as any).text !== undefined) updateData.note = (params as any).text;

  const result = await supabase
    .from('interview_meeting_notes')
    .update(updateData)
    .eq('id', interviewNoteId)
    .eq('interview_id', interviewId)
    .select()
    .single();

  return asResponse(throwIfError(result));
};

const getTechnicalChallenges = async (): Promise<AxiosResponse<TechnicalChallenge[]>> => {
  const result = await supabase
    .from('technical_challenges')
    .select('*');

  return asResponse(throwIfError(result));
};

function mapReviewerRow(row: any): any {
  if (!row) return null;
  const ir = row.interviewReviewer;
  return {
    id: row.id,
    status: row.status,
    interviewReviewer: ir ? {
      id: ir.id,
      person: mapPerson(ir.person),
    } : null,
    interviewTechnicalChallenge: row.interviewTechnicalChallenge ?? null,
  };
}

function mapSuggestionRow(row: any): any {
  if (!row) return null;
  const p = row.person;
  return {
    id: row.id,
    person: p ? {
      ...mapPerson(p),
      skills: (p.skills ?? []).map((ps: any) => ({
        ...ps,
        skill: ps.skill,
      })),
    } : null,
  };
}

const getTechnicalChallengesSuggestions = async (
  technicalChallengeId: string
): Promise<AxiosResponse<TechnicalChallengeSuggestions[]>> => {
  const result = await supabase
    .from('interview_reviewers')
    .select(`
      id,
      person:profiles!profile_id(*, skills:profile_skills(*, skill:skills(*)))
    `);

  const rows = throwIfError(result) as any[];
  return asResponse(rows.map(mapSuggestionRow) as any);
};

const getAllTechnicalChallengesReviewers = async (): Promise<AxiosResponse<TechnicalChallengeSuggestions[]>> => {
  const result = await supabase
    .from('interview_reviewers')
    .select(`
      id,
      person:profiles!profile_id(*, skills:profile_skills(*, skill:skills(*)))
    `);

  const rows = throwIfError(result) as any[];
  return asResponse(rows.map(mapSuggestionRow) as any);
};

const getTechnicalChallengeReviewers = async (
  technicalChallengeId: string
): Promise<AxiosResponse<TechnicalChallengeInterviewReviewers[]>> => {
  const result = await supabase
    .from('interview_technical_challenge_reviewers')
    .select(`
      id, status,
      interviewReviewer:interview_reviewers!reviewer_id(id, person:profiles!profile_id(id, first_name, last_name, picture_url)),
      interviewTechnicalChallenge:interview_technical_challenges!inner(*)
    `)
    .eq('interview_technical_challenges.technical_challenge_id', technicalChallengeId);

  const rows = throwIfError(result) as any[];
  return asResponse(rows.map(mapReviewerRow) as any);
};

const getTeamInterviewReviewers = async (
  teamInterviewId: string
): Promise<AxiosResponse<TechnicalChallengeInterviewReviewers[]>> => {
  const result = await supabase
    .from('team_interview_reviewers')
    .select(`
      id, status,
      interviewReviewer:interview_reviewers!reviewer_id(id, person:profiles!profile_id(id, first_name, last_name, picture_url))
    `)
    .eq('interview_id', teamInterviewId);

  const rows = throwIfError(result) as any[];
  return asResponse(rows.map(mapReviewerRow) as any);
};

export type AddReviewerToInterviewType = {
  interviewId: string;
  interviewReviewerId: string;
};

const addTechnicalChallengeReviewer = async ({
  interviewId,
  interviewReviewerId,
}: AddReviewerToInterviewType): Promise<AxiosResponse<void>> => {
  const result = await supabase
    .from('interview_technical_challenge_reviewers')
    .insert({
      interview_technical_challenge_id: interviewId,
      reviewer_id: interviewReviewerId,
      status: 'waiting',
    });

  throwIfError(result);
  return asResponse(undefined as unknown as void);
};

const addTeamInterviewReviewer = async ({
  interviewReviewerId,
  interviewId,
}: AddReviewerToInterviewType): Promise<AxiosResponse<void>> => {
  const result = await supabase
    .from('team_interview_reviewers')
    .insert({
      interview_id: interviewId,
      reviewer_id: interviewReviewerId,
    });

  throwIfError(result);
  return asResponse(undefined as unknown as void);
};

export type DeleteTeamInterviewReviewerType = {
  teamInterviewId: string;
  teamInterviewReviewerId: string;
};

const deleteTechnicalChallengeReviewer = async (personId: string): Promise<AxiosResponse<void>> => {
  const result = await supabase
    .from('interview_technical_challenge_reviewers')
    .delete()
    .eq('id', personId);

  throwIfError(result);
  return asResponse(undefined as unknown as void);
};

const deleteTeamInterviewReviewer = async ({
  teamInterviewId,
  teamInterviewReviewerId,
}: DeleteTeamInterviewReviewerType): Promise<AxiosResponse<void>> => {
  const result = await supabase
    .from('team_interview_reviewers')
    .delete()
    .eq('id', teamInterviewReviewerId)
    .eq('interview_id', teamInterviewId);

  throwIfError(result);
  return asResponse(undefined as unknown as void);
};

export type UploadInterviewRecordingType = {
  interviewId: string;
  recordingFile: File;
  onUploadProgress?: (progressEvent: { loaded: number; total: number }) => void;
};

const uploadInterviewRecording = async ({
  interviewId,
  recordingFile,
  onUploadProgress,
}: UploadInterviewRecordingType): Promise<AxiosResponse<InterviewType>> => {
  // Upload file to Supabase Storage
  const filePath = `interviews/${interviewId}/${recordingFile.name}`;
  const uploadResult = await supabase.storage
    .from('recordings')
    .upload(filePath, recordingFile, { upsert: true });

  if (uploadResult.error) throw uploadResult.error;

  // Get public URL
  const { data: urlData } = supabase.storage
    .from('recordings')
    .getPublicUrl(filePath);

  // Store the URL on the interview record
  const result = await supabase
    .from('interviews')
    .update({ recording_url: urlData.publicUrl })
    .eq('id', interviewId)
    .select()
    .single();

  return asResponse(throwIfError(result));
};

export type DeleteInterviewRecordingType = {
  interviewId: string;
};

const deleteInterviewRecording = async ({
  interviewId,
}: DeleteInterviewRecordingType): Promise<AxiosResponse<InterviewType>> => {
  // Get current recording URL to clean up storage
  const { data: interview } = await supabase
    .from('interviews')
    .select('recording_url')
    .eq('id', interviewId)
    .single();

  if (interview?.recording_url) {
    const match = interview.recording_url.match(/recordings\/(.+?)(?:\?|$)/);
    if (match) {
      await supabase.storage.from('recordings').remove([decodeURIComponent(match[1])]);
    }
  }

  const result = await supabase
    .from('interviews')
    .update({ recording_url: null })
    .eq('id', interviewId)
    .select()
    .single();

  return asResponse(throwIfError(result));
};

export type GetMyReviewsInterviewType = 'TEAM_INTERVIEW' | 'TECHNICAL_CHALLENGE';

export type GetMyReviewsOptions = {
  interviewType: GetMyReviewsInterviewType;
  interviewReviewStatus?: 'WAITING' | 'IN_PROGRESS' | 'DONE';
  take: number;
  page: number;
};

export type GetMyReviewsObject = {
  candidateFirstName: string;
  candidateLastName: string;
  candidateId: string;
  candidatePictureUrl: string | null;
  interviewTitle: string;
  interviewDate: string;
  interviewId: string;
  interviewTechnicalChallengeId?: string;
  reviewStatus: string;
  interviewType: string;
  reviewDenialsAmount: number;
  technicalChallengeReviewerPersonId?: string;
  reviewLimitDate: string;
  assignedAt?: string;
};

const getMyReviews = async ({
  interviewType,
  interviewReviewStatus,
  take,
  page,
}: GetMyReviewsOptions): Promise<AxiosResponse<ResponseWithPagination<GetMyReviewsObject[]>>> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  // Find the interview_reviewer record for the current user
  const { data: reviewer } = await supabase
    .from('interview_reviewers')
    .select('id')
    .eq('profile_id', user.id)
    .maybeSingle();

  if (!reviewer) {
    return asResponse<ResponseWithPagination<GetMyReviewsObject[]>>({
      data: [], count: 0, currentPage: page, lastPage: 1, nextPage: null, prevPage: null,
    });
  }

  const [offset] = paginationRange(page, take);

  // Query technical challenge reviewer assignments
  let query = supabase
    .from('interview_technical_challenge_reviewers')
    .select(`
      id, status, created_at,
      interviewTechnicalChallenge:interview_technical_challenges!inner(
        id,
        interview:interviews!inner(
          id, title, type, created_at,
          candidate:profiles!profile_id(id, first_name, last_name, picture_url)
        )
      )
    `, { count: 'exact' })
    .eq('reviewer_id', reviewer.id);

  if (interviewReviewStatus) {
    query = query.eq('status', interviewReviewStatus);
  }

  const result = await query
    .range(offset, offset + take - 1)
    .order('created_at', { ascending: false });

  const data = throwIfError(result) as any[];
  const count = (result as any).count || 0;
  const lastPage = Math.max(1, Math.ceil(count / take));

  const reviews: GetMyReviewsObject[] = data.map((row: any) => {
    const itc = row.interviewTechnicalChallenge;
    const interview = itc?.interview;
    return {
      candidateFirstName: interview?.candidate?.first_name || '',
      candidateLastName: interview?.candidate?.last_name || '',
      candidateId: interview?.candidate?.id || '',
      candidatePictureUrl: interview?.candidate?.picture_url || null,
      interviewTitle: interview?.title || '',
      interviewDate: interview?.created_at || '',
      interviewId: interview?.id || '',
      interviewTechnicalChallengeId: itc?.id,
      reviewStatus: row.status || '',
      interviewType: interview?.type || '',
      reviewDenialsAmount: 0,
      technicalChallengeReviewerPersonId: row.id,
      reviewLimitDate: '',
      assignedAt: row.created_at,
    };
  });

  const responseData: ResponseWithPagination<GetMyReviewsObject[]> = {
    data: reviews,
    count,
    currentPage: page,
    lastPage,
    nextPage: page < lastPage ? page + 1 : null,
    prevPage: page > 1 ? page - 1 : null,
  };

  return asResponse(responseData);
};

export default {
  deleteTeamInterviewReviewer,
  addTeamInterviewReviewer,
  getTeamInterviewReviewers,
  getAllTechnicalChallengesReviewers,
  getTechnicalChallengeReviewers,
  deleteTechnicalChallengeReviewer,
  addTechnicalChallengeReviewer,
  getTechnicalChallengesSuggestions,
  postCandidatePublicInterview,
  getCandidatePublicInterview,
  getCandidateInterviews,
  createInterview,
  updateInterview,
  deleteInterview,
  addQuestionInterview,
  deleteQuestionInterview,
  updateQuestionInterview,
  sendInterview,
  addReviewInterview,
  deleteReviewInterview,
  updateReviewInterview,
  updateReviewAiOverview,
  addNoteInterview,
  deleteNoteInterview,
  updateNoteInterview,
  getTechnicalChallenges,
  sendCandidatePublicTechnicalInterview,
  addTechnicalValidationReviewInterview,
  updateTechnicalValidationReviewInterview,
  uploadInterviewRecording,
  deleteInterviewRecording,
  getMyReviews,
  createTechnicalInterview,
};
